import { ArrowLeft, Shield, Lock, Eye, Database, Share2, Trash2, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Privacy() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white dark:bg-[#1D1D1F]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 dark:bg-[#1D1D1F]/95 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
          </button>
          <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white">Informativa Privacy</h1>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
            <Shield className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white mb-2">
            La tua privacy è importante
          </h1>
          <p className="text-[#6E6E73] dark:text-gray-400">
            Ultimo aggiornamento: Marzo 2024
          </p>
        </div>
        
        {/* Content */}
        <div className="space-y-8 text-[#1D1D1F] dark:text-gray-200">
          {/* Intro */}
          <section>
            <p className="text-[#6E6E73] dark:text-gray-400 leading-relaxed">
              Zampa ("noi", "nostro" o "Servizio") si impegna a proteggere la privacy degli utenti. 
              Questa informativa descrive come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali 
              quando utilizzi la nostra applicazione per la gestione del fascicolo sanitario digitale del tuo animale domestico.
            </p>
          </section>
          
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                <Database className="text-blue-600 dark:text-blue-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">1. Dati che raccogliamo</h2>
            </div>
            <div className="pl-13 space-y-3 text-[#6E6E73] dark:text-gray-400">
              <p><strong className="text-[#1D1D1F] dark:text-white">Dati dell'account:</strong> Nome, email, password (criptata), foto profilo.</p>
              <p><strong className="text-[#1D1D1F] dark:text-white">Dati dell'animale:</strong> Nome, specie, razza, data di nascita, microchip, foto, documentazione sanitaria.</p>
              <p><strong className="text-[#1D1D1F] dark:text-white">Dati sanitari:</strong> Certificati vaccinali, referti medici, prescrizioni, passaporto dell'animale.</p>
              <p><strong className="text-[#1D1D1F] dark:text-white">Dati di utilizzo:</strong> Log di accesso, dispositivo utilizzato, interazioni con l'app.</p>
            </div>
          </section>
          
          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                <Eye className="text-emerald-600 dark:text-emerald-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">2. Come utilizziamo i tuoi dati</h2>
            </div>
            <ul className="pl-13 space-y-2 text-[#6E6E73] dark:text-gray-400 list-disc list-inside">
              <li>Fornire e migliorare il servizio di fascicolo sanitario digitale</li>
              <li>Permettere la condivisione sicura con veterinari e autorità</li>
              <li>Inviare promemoria per vaccini e visite mediche</li>
              <li>Generare il QR code per l'accesso rapido ai dati</li>
              <li>Garantire la sicurezza e prevenire frodi</li>
            </ul>
          </section>
          
          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                <Share2 className="text-purple-600 dark:text-purple-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">3. Condivisione dei dati</h2>
            </div>
            <div className="pl-13 space-y-3 text-[#6E6E73] dark:text-gray-400">
              <p>I tuoi dati <strong className="text-[#1D1D1F] dark:text-white">non vengono mai venduti</strong> a terzi.</p>
              <p>Condividiamo i dati solo:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Con veterinari/professionisti da te esplicitamente autorizzati tramite QR code o link</li>
                <li>Con autorità competenti se richiesto dalla legge</li>
                <li>Con fornitori tecnici essenziali per il funzionamento del servizio (hosting, email)</li>
              </ul>
            </div>
          </section>
          
          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <Lock className="text-amber-600 dark:text-amber-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">4. Sicurezza dei dati</h2>
            </div>
            <ul className="pl-13 space-y-2 text-[#6E6E73] dark:text-gray-400 list-disc list-inside">
              <li>Crittografia dei dati in transito (HTTPS/TLS)</li>
              <li>Password hashate con algoritmi sicuri</li>
              <li>Autenticazione a due fattori (2FA) disponibile</li>
              <li>Accesso limitato ai dati da parte del personale</li>
              <li>Backup regolari e sicuri</li>
            </ul>
          </section>
          
          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                <Trash2 className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <h2 className="text-lg font-bold">5. I tuoi diritti (GDPR)</h2>
            </div>
            <div className="pl-13 space-y-3 text-[#6E6E73] dark:text-gray-400">
              <p>Hai il diritto di:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong className="text-[#1D1D1F] dark:text-white">Accesso:</strong> Richiedere una copia dei tuoi dati</li>
                <li><strong className="text-[#1D1D1F] dark:text-white">Rettifica:</strong> Correggere dati inesatti</li>
                <li><strong className="text-[#1D1D1F] dark:text-white">Cancellazione:</strong> Richiedere l'eliminazione dei tuoi dati</li>
                <li><strong className="text-[#1D1D1F] dark:text-white">Portabilità:</strong> Esportare i tuoi dati in formato leggibile</li>
                <li><strong className="text-[#1D1D1F] dark:text-white">Opposizione:</strong> Opporti a determinati trattamenti</li>
              </ul>
            </div>
          </section>
          
          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
                <Mail className="text-[#FF6B4A]" size={20} />
              </div>
              <h2 className="text-lg font-bold">6. Contatti</h2>
            </div>
            <div className="pl-13 text-[#6E6E73] dark:text-gray-400">
              <p>Per esercitare i tuoi diritti o per qualsiasi domanda sulla privacy:</p>
              <p className="mt-2">
                <strong className="text-[#1D1D1F] dark:text-white">Email:</strong>{" "}
                <a href="mailto:privacy@zampa.one" className="text-[#FF6B4A] underline">privacy@zampa.one</a>
              </p>
            </div>
          </section>
        </div>
        
        {/* Footer note */}
        <div className="mt-12 p-4 bg-[#F5F5F7] dark:bg-white/5 rounded-2xl">
          <p className="text-sm text-[#86868B] dark:text-gray-500 text-center">
            Questa informativa può essere aggiornata periodicamente. Ti informeremo di eventuali modifiche significative.
          </p>
        </div>
      </main>
    </div>
  );
}
